import type { ActiveNetworkBranding } from '../store';

/**
 * The base64-encoded GraphQL ID for the default theme.
 * Decodes to: {"_type":"NetworkBranding","id":"-1"}
 * This is treated as "no custom branding" and should allow M365 organizational themes.
 */
export const defaultThemeEncodedId = 'eyJfdHlwZSI6Ik5ldHdvcmtCcmFuZGluZyIsImlkIjoiLTEifQ';

/**
 * Determines if a brand theme is the "default" theme (no custom branding).
 *
 * The default theme has a specific encoded ID that indicates no custom brand is set.
 * When detected, M365 organizational themes should be allowed through.
 *
 * @param brandTheme - The brand theme to check
 * @returns true if this is the default theme, false otherwise
 */
export const isDefaultTheme = (brandTheme: ActiveNetworkBranding | null | undefined): boolean => {
  if (!brandTheme?.id) {
    return false;
  }

  // Compare the encoded ID directly (treating GraphQL IDs as opaque strings)
  return brandTheme.id === defaultThemeEncodedId;
};

/**
 * Determines if a brand theme is a valid custom brand (not default, has tokens).
 * This is a type guard that narrows the type to ensure tokensAndStyles exists.
 *
 * @param brandTheme - The brand theme to check
 * @returns true if this is a valid custom brand theme, false otherwise
 */
export const isValidCustomBrandTheme = (
  brandTheme: ActiveNetworkBranding | null | undefined
): brandTheme is ActiveNetworkBranding & {
  readonly tokensAndStyles: NonNullable<ActiveNetworkBranding['tokensAndStyles']>;
} => !!(brandTheme?.tokensAndStyles && !isDefaultTheme(brandTheme));
